'use client';

import { useEffect, useState } from 'react';
import dynamic from 'next/dynamic';
import { Box, CircularProgress, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { createThemeFromTokens } from '@fast-computing/fast-graphics/mui-theme';

const Plot = dynamic(() => import('react-plotly.js'), { ssr: false });
const selectedTheme = createThemeFromTokens('atlas', {
  withComponentDefaults: true,
});

type FlowRateResponse = {
  message: string;
  requested_bc: number;
  time: number[];
  default_flow_rate: number[];
  flow_rate: number[];
};

type FlowRatePlotProps = {
  timeROM: number;
  bcROM: number;
};

export default function FlowRatePlot({ timeROM, bcROM }: FlowRatePlotProps) {
  const [data, setData] = useState<FlowRateResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const baseFontSize = 14;
  const axisTitleFontSize = 16;
  const legendFontSize = 12;
  const plotBackgroundColor = `${selectedTheme.palette.background.paper}`;
  const gridColor = alpha(selectedTheme.palette.text.primary, 0.12);
  const axisColor = alpha(selectedTheme.palette.text.primary, 0.3);

  useEffect(() => {
    const controller = new AbortController();

    const fetchFlowData = async () => {
      setLoading(true);
      setError(null);

      try {
        const token = localStorage.getItem('access_token');
        const url = `${process.env.NEXT_PUBLIC_API_URL}/get_bc_data?bc_ROM=${bcROM}`;
        const response = await fetch(url, {
          method: 'GET',
          signal: controller.signal,
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        if (!response.ok) {
          throw new Error('Unable to load flow-rate data.');
        }

        const responseData = (await response.json()) as FlowRateResponse;
        setData(responseData);
      } catch (fetchError) {
        if ((fetchError as Error).name === 'AbortError') {
          return;
        }

        setError('Unable to load flow-rate data.');
      } finally {
        setLoading(false);
      }
    };

    fetchFlowData();

    return () => {
      controller.abort();
    };
  }, [bcROM]);

  if (loading && !data) {
    return (
      <Box
        sx={{
          width: '100%',
          height: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <CircularProgress size={24} />
      </Box>
    );
  }

  if (error || !data) {
    return (
      <Box
        sx={{
          width: '100%',
          height: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          px: 2,
          textAlign: 'center',
        }}
      >
        <Typography variant='body2' color='error'>
          {error ?? 'No flow-rate data available.'}
        </Typography>
      </Box>
    );
  }

  return (
    <Plot
      data={[
        {
          x: data.time,
          y: data.default_flow_rate,
          type: 'scatter',
          mode: 'lines',
          name: 'Flux Intensity = 1',
          line: {
            color: alpha(selectedTheme.palette.primary.light, 0.5),
            width: 2,
          },
        },
        {
          x: data.time,
          y: data.flow_rate,
          type: 'scatter',
          mode: 'lines',
          name: 'Selected Flux Intensity',
          line: {
            color: alpha(selectedTheme.palette.primary.main, 1),
            width: 3,
          },
        },
      ]}
      layout={{
        autosize: true,
        paper_bgcolor: 'transparent',
        plot_bgcolor: plotBackgroundColor,
        margin: { l: 55, r: 20, t: 56, b: 52 },
        font: {
          family: `${selectedTheme.typography.fontFamily}`,
          size: baseFontSize,
          color: `${selectedTheme.palette.text.primary}`,
        },
        xaxis: {
          title: {
            text: 'Cardiac Cycle [s]',
            font: {
              size: axisTitleFontSize,
              color: `${selectedTheme.palette.text.primary}`,
            },
          },
          tickfont: {
            size: baseFontSize,
            color: `${selectedTheme.palette.text.secondary}`,
          },
          gridcolor: gridColor,
          linecolor: axisColor,
          zerolinecolor: gridColor,
          autorange: true,
        },
        yaxis: {
          title: {
            text: 'Mitral Valve Flow Rate [ml/s]',
            font: {
              size: axisTitleFontSize,
              color: `${selectedTheme.palette.text.primary}`,
            },
          },
          tickfont: {
            size: baseFontSize,
            color: `${selectedTheme.palette.text.secondary}`,
          },
          gridcolor: gridColor,
          linecolor: axisColor,
          zerolinecolor: gridColor,
          autorange: false,
          range: [0.0, 400.0],
        },
        legend: {
          orientation: 'h',
          x: 0,
          y: 1.08,
          xanchor: 'left',
          yanchor: 'bottom',
          font: {
            size: legendFontSize,
            color: `${selectedTheme.palette.text.primary}`,
          },
        },
        shapes: [
          {
            type: 'line',
            x0: timeROM,
            x1: timeROM,
            y0: 0.0,
            y1: 400.0,
            line: {
              color: `${selectedTheme.palette.secondary.main}`,
              width: 2,
            },
          },
        ],
      }}
      config={{
        displaylogo: false,
        responsive: true,
        doubleClick: 'reset+autosize',
      }}
      style={{ width: '100%', height: '100%' }}
      useResizeHandler
    />
  );
}
